import * as React from "react";
import { DayPicker, UI, DayFlag, SelectionState } from "react-day-picker";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { cn } from "~/utils/fontsConfig";
import { buttonVariants } from "./Button";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
    theme?: "dark" | "light";
};

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    theme = "dark",
    ...props
}: CalendarProps) {
    if (theme === "light") {
        return (
            <DayPicker
                showOutsideDays={showOutsideDays}
                className={cn("p-3", className)}
                classNames={{
                    [UI.Months]:
                        "relative flex flex-col sm:flex-row flex space-y-4 sm:space-x-0 sm:space-y-0",
                    [UI.Month]: "space-y-4 ml-0",
                    [UI.MonthCaption]: "flex justify-center items-center h-7 ",
                    [UI.CaptionLabel]: "text-sm font-medium",
                    [UI.PreviousMonthButton]: cn(
                        buttonVariants({ variant: "outline" }),
                        "absolute left-1 hover:bg-black hover:text-white top-0 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 rounded-full",
                    ),
                    [UI.NextMonthButton]: cn(
                        buttonVariants({ variant: "outline" }),
                        "absolute right-1 hover:bg-black hover:text-white top-0 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 rounded-full",
                    ),
                    [UI.MonthGrid]: "w-full border-collapse space-y-1",
                    [UI.Weekdays]: "flex",
                    [UI.Weekday]:
                        "text-gray-500 rounded-md w-9 font-normal text-[0.8rem]",
                    [UI.Week]: "flex w-full mt-2",
                    [UI.Day]: cn(
                        "h-9 w-9 mx-[1px] text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
                        "relative before:content-[''] before:absolute before:bottom-1 before:left-1/2 before:transform before:-translate-x-1/2 before:w-1 before:h-1 before:rounded-full",
                    ),
                    [UI.DayButton]: cn(
                        buttonVariants({ variant: "ghost" }),
                        "h-9 w-9 rounded-full p-0 hover:bg-black hover:text-white font-normal aria-selected:opacity-100",
                    ),
                    [UI.Dropdowns]: "flex items-center gap-1",
                    [SelectionState.range_end]: "day-range-end",
                    [SelectionState.selected]:
                        "text-white p-0 hover:text-white bg-black focus:text-white rounded-full",
                    [SelectionState.range_middle]:
                        "aria-selected:bg-gray-200 aria-selected:text-black",
                    [DayFlag.today]: "bg-gray-200 text-black rounded-full",
                    [DayFlag.outside]: "text-black opacity-70",
                    [DayFlag.disabled]: "text-black opacity-70",
                    [DayFlag.hidden]: "invisible",
                    ...classNames,
                }}
                components={{
                    Chevron: ({ orientation }) => {
                        if (orientation === "left") {
                            return <LuChevronLeft className="h-4 w-4" />;
                        }

                        return <LuChevronRight className="h-4 w-4" />;
                    },
                }}
                {...props}
            />
        );
    }

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn("p-3", className)}
            classNames={{
                [UI.Months]:
                    "relative flex flex-col sm:flex-row flex space-y-4 sm:space-x-0 sm:space-y-0",
                [UI.Month]: "space-y-4 ml-0",
                [UI.MonthCaption]: "flex justify-center items-center h-7 ",
                [UI.CaptionLabel]: "text-sm font-medium",
                [UI.PreviousMonthButton]: cn(
                    buttonVariants({ variant: "outline" }),
                    "absolute left-1 hover:bg-black hover:text-white top-0 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 rounded-full",
                ),
                [UI.NextMonthButton]: cn(
                    buttonVariants({ variant: "outline" }),
                    "absolute right-1 hover:bg-black hover:text-white top-0 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 rounded-full",
                ),
                [UI.MonthGrid]: "w-full border-collapse space-y-1",
                [UI.Weekdays]: "flex",
                [UI.Weekday]:
                    "text-gray-500 rounded-md w-9 font-normal text-[0.8rem]",
                [UI.Week]: "flex w-full mt-2",
                [UI.Day]: cn(
                    "h-9 w-9 mx-[1px] text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
                    "relative before:content-[''] before:absolute before:bottom-1 before:left-1/2 before:transform before:-translate-x-1/2 before:w-1 before:h-1 before:rounded-full",
                ),
                [UI.DayButton]: cn(
                    buttonVariants({ variant: "ghost" }),
                    "h-9 w-9 rounded-full p-0 hover:bg-black hover:text-white font-normal aria-selected:opacity-100",
                ),
                [UI.Dropdowns]: "flex items-center gap-1",
                [SelectionState.range_end]: "day-range-end",
                [SelectionState.selected]:
                    "text-white p-0 hover:text-white bg-black focus:text-white rounded-full",
                [SelectionState.range_middle]:
                    "aria-selected:bg-gray-200 aria-selected:text-black",
                [DayFlag.today]: "bg-gray-200 text-black rounded-full",
                [DayFlag.outside]: "text-gray-400 opacity-50",
                [DayFlag.disabled]: "text-gray-400 opacity-50",
                [DayFlag.hidden]: "invisible",
                ...classNames,
            }}
            components={{
                Chevron: ({ orientation }) => {
                    if (orientation === "left") {
                        return <LuChevronLeft className="h-4 w-4" />;
                    }

                    return <LuChevronRight className="h-4 w-4" />;
                },
            }}
            {...props}
        />
    );
}

Calendar.displayName = "Calendar";

export { Calendar };
